/**
 * MKS-163 — task priority picker for TaskDetailSlideOver.
 *
 * Structurally mirrors TaskTierPicker.tsx (same popover layout, same z-index,
 * same useClickOutside + separate Esc useEffect, same role="listbox"
 * semantics, same onError callback prop). Differences:
 *   - Drives useUpdateTask (the PATCH /api/tasks/:id mutation) instead of
 *     useUpdateTaskTier — priority has no dedicated endpoint (D-1/D-5).
 *   - Trigger and options render via the existing MtPriorityBadge /
 *     priorityConfig palette (components/mytasks/MtPriorityBadge) — no
 *     second palette introduced.
 *   - Selecting the already-current value is a no-op close (mirrors
 *     TaskTierPicker's same-tier early return), which also spares the
 *     backend a request the service would skip-audit anyway (T-o8n-04).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useUpdateTask } from '../../hooks/useUpdateTask';
import { useClickOutside } from '../../hooks/useClickOutside';
import { MtPriorityBadge, priorityConfig } from '../mytasks/MtPriorityBadge';
import type { TaskPriority } from '../../services/api/board';

// MKS-163 (HR-2) — mirrors TASK_PRIORITIES in
// backend/src/api/schemas/taskMutation.ts.
const TASK_PRIORITIES: TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

export interface TaskPriorityPickerProps {
  taskId: number;
  projectId: number;
  currentPriority: TaskPriority;
  canManage: boolean;
  onError?: (message: string) => void;
}

export function TaskPriorityPicker({
  taskId,
  projectId,
  currentPriority,
  canManage,
  onError,
}: TaskPriorityPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mutation = useUpdateTask();

  useClickOutside(containerRef, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleSelect = useCallback(
    (toPriority: TaskPriority) => {
      if (toPriority === currentPriority) {
        setOpen(false);
        return;
      }
      mutation.mutate(
        {
          taskId,
          projectId: String(projectId),
          priority: toPriority,
        },
        {
          onError: (err) => {
            if (onError) onError(err.message ?? 'Server error');
          },
        },
      );
      setOpen(false);
    },
    [mutation, taskId, projectId, currentPriority, onError],
  );

  const cfg = priorityConfig(currentPriority);

  // Read-only badge when the viewer has no rights to change priority.
  if (!canManage) {
    return (
      <span data-testid="task-priority-picker">
        <MtPriorityBadge priority={currentPriority} withLabel />
      </span>
    );
  }

  const isPending = mutation.isPending;

  return (
    <div ref={containerRef} className="relative shrink-0" data-testid="task-priority-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Change priority (current: ${cfg.label})`}
        className="inline-flex items-center gap-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60 hover:ring-2 hover:ring-ms-border"
        data-testid="task-priority-picker-trigger"
      >
        <MtPriorityBadge priority={currentPriority} withLabel />
        <ChevronDownIcon className="w-3.5 h-3.5 text-ms-text-muted" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Task priority options"
          className="absolute right-0 mt-1 w-40 rounded-lg border border-ms-border bg-white shadow-lg z-50 py-1"
          data-testid="task-priority-picker-popover"
        >
          {TASK_PRIORITIES.map((priority) => {
            const selected = priority === currentPriority;
            const optionCfg = priorityConfig(priority);
            return (
              <button
                key={priority}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => handleSelect(priority)}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-ms-canvas focus:bg-ms-canvas focus:outline-none"
                data-testid={`task-priority-option-${priority}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <MtPriorityBadge priority={priority} withLabel={false} />
                  <span className="text-sm text-ms-text truncate">{optionCfg.label}</span>
                </span>
                {selected && (
                  <CheckIcon
                    className="w-4 h-4 text-ms-text-secondary shrink-0"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
